import { useState, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Check, X, UserPlus, Users, ChevronRight, Link } from 'lucide-react';
import { SkeletonBlock } from '../Skeleton';
import { API_URL } from '../../utils/api';
import { useAppNavigate } from '../../contexts/PwaNavContext';
import type { FriendsData, FriendCard } from '../../types/passport';

// ── FriendSearch ──────────────────────────────────────────────────────────────

interface SearchResult {
  id: string;
  username: string;
  displayName: string;
  level: string;
  friendStatus: string | null;
}

function FriendSearch({ token, onRequestSent }: { token: string | null; onRequestSent: () => void }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [sendingTo, setSendingTo] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const handleSearch = (q: string) => {
    setQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.length < 2) { setResults([]); return; }

    debounceRef.current = setTimeout(async () => {
      if (!token) return;
      setSearching(true);
      try {
        const { data } = await axios.get(`${API_URL || ''}/api/friends/search`, {
          params: { q },
          headers: { Authorization: `Bearer ${token}` },
        });
        setResults(data.results || []);
      } catch { setResults([]); }
      setSearching(false);
    }, 350);
  };

  const handleAdd = async (userId: string) => {
    if (!token || sendingTo) return;
    setSendingTo(userId);
    try {
      await axios.post(`${API_URL || ''}/api/friends/invite`, { userId }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setResults(prev => prev.map(r => r.id === userId ? { ...r, friendStatus: 'PENDING' } : r));
      onRequestSent();
    } catch { /* */ }
    setSendingTo(null);
  };

  return (
    <div className="pp-search-block">
      <div className="pp-search-wrap">
        <span className="pp-search-icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
        </span>
        <input
          className="pp-search-input"
          placeholder={t('friends.searchPlaceholder', 'Search by username...')}
          value={query}
          onChange={e => handleSearch(e.target.value.toLowerCase())}
          autoCapitalize="off"
          autoCorrect="off"
        />
        {searching && <span className="pp-search-spinner" />}
      </div>
      {results.length > 0 && (
        <div className="pp-search-results">
          {results.map(r => (
            <div key={r.id} className="pp-search-result">
              <div className="pp-friend-avatar">{r.displayName.slice(0, 2).toUpperCase()}</div>
              <div className="pp-friend-info">
                <div className="pp-friend-name">@{r.username}</div>
                <div className="pp-friend-level">{r.level}</div>
              </div>
              {r.friendStatus === 'ACCEPTED' ? (
                <span className="pp-search-status">{t('friends.alreadyFriends', 'Friends')}</span>
              ) : r.friendStatus === 'PENDING' ? (
                <span className="pp-search-status">{t('friends.pending', 'Pending')}</span>
              ) : (
                <button
                  className="pp-search-add-btn"
                  disabled={sendingTo === r.id}
                  onClick={() => handleAdd(r.id)}
                >
                  <UserPlus size={16} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── FriendsView ───────────────────────────────────────────────────────────────

interface FriendsViewProps {
  friendsData: FriendsData | null;
  friendsLoading: boolean;
  inviteCopied: boolean;
  effectiveToken: string | null;
  onCopyInvite: () => void;
  onAcceptFriend: (id: string) => Promise<void>;
  onDeclineFriend: (id: string) => Promise<void>;
  onUnfriend: (id: string) => Promise<void>;
  fetchFriends: () => Promise<void>;
}

export default function FriendsView({
  friendsData,
  friendsLoading,
  inviteCopied,
  effectiveToken,
  onCopyInvite,
  onAcceptFriend,
  onDeclineFriend,
  onUnfriend,
  fetchFriends,
}: FriendsViewProps) {
  const { t } = useTranslation();
  const navigate = useAppNavigate();
  const [selectedFriend] = useState<FriendCard | null>(null);

  return (
    <div className="pp-friends-view pp-tab-content">
      <div className="pp-invite-block">
        <div className="pp-invite-label">{t('friends.yourCode')}</div>
        <div className="pp-invite-code">{friendsData?.referralCode || '—'}</div>
        <button className="pp-invite-btn" onClick={onCopyInvite}>
          {inviteCopied ? <><Check size={14} /> {t('friends.copied')}</> : <><Link size={14} /> {t('friends.copyLink')}</>}
        </button>
        <div className="pp-invite-bonus">{t('friends.bonusHint', { points: 50 })}</div>
      </div>

      {/* ── Friend Search ── */}
      <FriendSearch token={effectiveToken} onRequestSent={fetchFriends} />

      {friendsData && friendsData.incoming.length > 0 && (
        <div className="pp-friends-section">
          <div className="pp-friends-section-title">{t('friends.incoming')}</div>
          {friendsData.incoming.map(({ friendshipId, friend }) => (
            <div key={friendshipId} className="pp-friend-request-card">
              <div className="pp-friend-avatar">
                {friend.photoURL
                  ? <img src={friend.photoURL} alt="" />
                  : friend.displayName.slice(0, 2).toUpperCase()}
              </div>
              <div className="pp-friend-info">
                <div className="pp-friend-name">{friend.displayName}</div>
                <div className="pp-friend-level">{friend.level}</div>
              </div>
              <div className="pp-friend-actions">
                <button className="pp-btn-accept" onClick={() => onAcceptFriend(friendshipId)}><Check size={16} /></button>
                <button className="pp-btn-decline" onClick={() => onDeclineFriend(friendshipId)}><X size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {friendsData && friendsData.outgoing.length > 0 && (
        <div className="pp-friends-section">
          <div className="pp-friends-section-title">{t('friends.outgoing', 'Sent requests')}</div>
          {friendsData.outgoing.map(({ friendshipId, friend }) => (
            <div key={friendshipId} className="pp-friend-request-card">
              <div className="pp-friend-avatar">
                {friend.photoURL
                  ? <img src={friend.photoURL} alt="" />
                  : friend.displayName.slice(0, 2).toUpperCase()}
              </div>
              <div className="pp-friend-info">
                <div className="pp-friend-name">{friend.displayName}</div>
                <div className="pp-friend-level pp-friend-level--pending">{t('friends.pending', 'Pending...')}</div>
              </div>
              <div className="pp-friend-actions">
                <button className="pp-btn-cancel" onClick={() => onUnfriend(friendshipId)} title={t('friends.cancelRequest', 'Cancel request')}><X size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="pp-friends-section">
        <div className="pp-friends-section-title">
          {t('friends.myFriends')} {friendsData?.friends.length ? `(${friendsData.friends.length})` : ''}
        </div>

        {friendsLoading ? (
          <div className="pp-friends-loading">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="pp-friend-card" style={{ pointerEvents: 'none' }}>
                <SkeletonBlock width="48px" height="48px" radius="50%" />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column' as const, gap: '6px' }}>
                  <SkeletonBlock width="120px" height="14px" radius="4px" />
                  <SkeletonBlock width="80px" height="10px" radius="4px" />
                </div>
              </div>
            ))}
          </div>
        ) : !friendsData?.friends.length ? (
          <div className="pp-friends-empty">
            <div className="pp-friends-empty-icon"><Users size={32} /></div>
            <div className="pp-friends-empty-text">{t('friends.empty')}</div>
          </div>
        ) : (
          friendsData.friends.map((friend) => (
            <div
              key={friend.id}
              className="pp-friend-card"
              style={{ cursor: 'pointer' }}
              onClick={() => navigate(`/friend-profile?friendId=${friend.id}`)}
            >
              <div className="pp-friend-avatar">
                {friend.photoURL
                  ? <img src={friend.photoURL} alt="" />
                  : friend.displayName.slice(0, 2).toUpperCase()}
              </div>
              <div className="pp-friend-info">
                <div className="pp-friend-name">{friend.displayName}</div>
                <div className="pp-friend-level">{friend.level} · {friend.totalPoints} pts</div>
              </div>
              <div className="pp-friend-chevron"><ChevronRight size={16} /></div>

              {selectedFriend?.id === friend.id && (
                <div className="pp-friend-passport" onClick={(e) => e.stopPropagation()}>
                  <div className="pp-friend-passport-label">{t('friends.stamps')}</div>
                  <div className="pp-friend-stamps">
                    {friend.stamps.length ? friend.stamps.map((stamp) => (
                      <div key={stamp.id} className="pp-friend-stamp">
                        <div className="pp-friend-stamp-emoji"></div>
                        <div className="pp-friend-stamp-name">{stamp.name.slice(0, 8)}</div>
                      </div>
                    )) : (
                      <div className="pp-friends-empty-text" style={{ fontSize: '12px' }}>{t('friends.noStamps')}</div>
                    )}
                  </div>
                  <div className="pp-friend-stats-row">
                    <div className="pp-friend-stat">
                      <span>{friend.totalOrders}</span>
                      <span>{t('foodPassport.orders')}</span>
                    </div>
                    <div className="pp-friend-stat">
                      <span>{friend.totalPoints}</span>
                      <span>{t('foodPassport.points')}</span>
                    </div>
                    <div className="pp-friend-stat">
                      <span>{friend.stamps.length}</span>
                      <span>{t('friends.restaurants')}</span>
                    </div>
                  </div>
                  <button
                    className="pp-btn-unfriend"
                    onClick={(e) => { e.stopPropagation(); onUnfriend(friend.friendshipId); }}
                  >
                    {t('friends.unfriend', 'Unfriend')}
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
